import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertCircle, ArrowLeft, Plus, Loader2, Trash2, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useAdminSubscriptions } from '../hooks';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function AdminSubscriptionsPage() {
  const [filterUserId, setFilterUserId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { subscriptions, total, loading, error, refresh } = useAdminSubscriptions({
    userId: filterUserId || undefined,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Form state for setting a subscription
  const [username, setUsername] = useState('');
  const [tier, setTier] = useState('bronze');
  const [durationDays, setDurationDays] = useState('30');

  const resetForm = () => {
    setUsername('');
    setTier('bronze');
    setDurationDays('30');
    setFormError(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterUserId(searchInput.trim());
  };

  const handleSetSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;

    setSaving(true);
    setFormError(null);
    try {
      await api.adminSetGameRewardsSubscription({
        username: username.trim(),
        tier,
        durationDays: Number(durationDays),
      });
      setDialogOpen(false);
      resetForm();
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to set subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (subscriptionId: string) => {
    setActionError(null);
    try {
      await api.adminRemoveGameRewardsSubscription(subscriptionId);
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove subscription');
    }
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const displayError = error || actionError;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Subscriptions</h1>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Set Subscription
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Set User Subscription</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSetSubscription} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subUsername">Username *</Label>
                  <Input
                    id="subUsername"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. devuser1"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tier *</Label>
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bronze">Bronze</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subDuration">Duration (days) *</Label>
                  <Input
                    id="subDuration"
                    type="number"
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                    required
                  />
                </div>

                {formError && <p className="text-sm text-destructive">{formError}</p>}

                <Button type="submit" className="w-full" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Set Subscription
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by user ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
            aria-label="Filter by user ID"
          />
        </div>
        <Button type="submit" variant="secondary">
          Filter
        </Button>
        {filterUserId && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setSearchInput('');
              setFilterUserId('');
            }}
          >
            Clear
          </Button>
        )}
      </form>

      {displayError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{displayError}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : subscriptions.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          {filterUserId ? 'No subscriptions found for this user.' : 'No subscriptions found.'}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{total} subscription(s) found</p>
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Starts</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => (
                    <TableRow key={sub.subscriptionId}>
                      <TableCell>{sub.username ?? sub.userId ?? '-'}</TableCell>
                      <TableCell>{sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1)}</TableCell>
                      <TableCell className="text-sm">{formatDate(sub.startsAt)}</TableCell>
                      <TableCell className="text-sm">{formatDate(sub.expiresAt)}</TableCell>
                      <TableCell>
                        <Badge variant={sub.isActive ? 'default' : 'secondary'}>
                          {sub.isActive ? 'Active' : 'Expired'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Subscription</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to remove this subscription? This action
                                cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleRemove(sub.subscriptionId)}>
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
